import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlQueryContentFieldDto} from 'features/core/shared/ql/data/dto/ql-query-content-field.dto';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionTransactionEvent} from '../../../../../enum/provision-transaction-event.enum';
import {ProvisionTransactionStatus} from '../../../../../enum/provision-transaction-status.enum';
import {ProvisionAgentBalanceDto} from '../../../../../model/dto/entity/provision-agent-balance.dto';
import {ProvisionAgentCoverageDto} from '../../../../../model/dto/api/provision-agent-coverage.dto';
import {CpProvisionAgentCoverageService} from '../../../../../service/api/cp-provision-agent-coverage.service';

/**
 * Field shape of the QL `dateGroup` field type.
 *
 * The backend knows three field types (`QlQueryContentFieldType`: regular | function | dateGroup)
 * and translates `dateGroup` into `DATE_TRUNC('<precision>', column)`. The shared frontend DTO
 * `QlQueryContentFieldDto` models only `regular` and `function`, so this local extension adds the
 * two missing attributes WITHOUT touching a core file.
 *
 * This is the SAME documented workaround the sibling pane already carries — see the Javadoc of
 * `CpQlDateGroupField` in `cp-provision-agent-payouts.component.ts`, which owns the full rationale
 * and the removal recipe. It is duplicated rather than shared because widening the core DTO is a
 * cross-module change and hoisting the interface into a cp-wide file would touch a file this work
 * package does not own. Both copies disappear together once `QlQueryContentFieldDto` itself carries
 * `fieldType: 'dateGroup'` and `dateGroupPrecision`.
 */
interface CpKpiQlDateGroupField extends Omit<QlQueryContentFieldDto, 'fieldType'> {
    fieldType: 'dateGroup';
    dateGroupPrecision: 'day' | 'month' | 'year';
}

/** The three chart tiles that can come back empty. */
type CpKpiChartKey = 'provisions' | 'payouts' | 'coverage';

/** One curated column/field of a KPI widget layout, with an explicit output control. */
interface CpKpiWidgetField {
    /** Field id: the plain attribute name on an entity widget, `e.<attr>` / `<joinAs>.<attr>` on a QL one. */
    field: string;
    /** German label, with real umlauts. */
    label: string;
    /** Registered output control, e.g. `currencyEuro`, `appendPercentage`. Omit for the default. */
    outputField?: string;
}

/**
 * navItem "Kennzahlen" of the ProvisionAgent OBC — the five KPI tiles of the Abrechnungskonto
 * (requirement §2 + capability-matrix FD-6).
 *
 * <h3>Every tile is a native widget</h3>
 * The design gate verified that a native `mvs-widget` mechanism exists for ALL five tiles, so a
 * hand-rolled KPI card is NOT justified here and none is built. In particular this pane is
 * deliberately NOT modelled on `cp-provision-dashboard-start.component.html`, which builds its KPI
 * cards by hand in PrimeFlex (`cp-kpi-card`, `cp-kpi-progress-track`) — a known legacy finding,
 * explicitly out of scope (abgrenzung A-4) and explicitly NOT a pattern to copy.
 *
 * <table>
 *   <tr><th>#</th><th>Tile</th><th>Mechanism</th></tr>
 *   <tr><td>1</td><td>Provisionen pro Monat</td>
 *       <td>`createWidgetListQl` uiComponent `chart` over a QL `groupBy` with the `dateGroup`
 *           field type (month of `valutaDate`) and `sum(amount)`.</td></tr>
 *   <tr><td>2</td><td>Auszahlungen pro Monat</td>
 *       <td>same query plus `event = PAYOUT`.</td></tr>
 *   <tr><td>3</td><td>Rücklagenkonto</td>
 *       <td>`createWidgetEntityData` on `cp.ProvisionAgentBalance`, layout-pinned to the two reserve
 *           columns.</td></tr>
 *   <tr><td>4</td><td>Rücklage vs. Haftung je Lauf</td>
 *       <td>`createWidgetTableQl` + `createWidgetListQl` chart over
 *           `cp.ProvisionAgentReserveCoverage`.</td></tr>
 *   <tr><td>5</td><td>Deckungsquote (live)</td>
 *       <td>`createWidgetTransient` uiComponent `data`, fed by
 *           `GET /mvsa/cp/provisionAgents/{id}/coverage`.</td></tr>
 * </table>
 *
 * `createWidgetGroupBy` is deliberately NOT used for tiles 1 and 2: it hard-codes a `count`
 * aggregate (`widget.factory.ts:890-896`) and therefore cannot deliver the monthly SUMS these tiles
 * are about.
 *
 * <h3>No metric is derived in the frontend</h3>
 * All four figures of tiles 4 and 5 — `openLiabilityAmount`, `reserveBalance`, `coverageQuote`,
 * `shortfallAmount` — are read as they come: persisted columns for the history, endpoint fields for
 * the live tile. The quote formula lives in `ProvisionReserveCoverageService.quoteOf` /
 * `.shortfallOf`; dividing two numbers here would be a second copy of a business rule
 * (capability-matrix row B1). This component contains no arithmetic on business figures at all.
 *
 * <h3>NULL coverage quote</h3>
 * `coverageQuote` is NULL exactly when the account carries no open liability. It must render as a
 * dash and NEVER as 0 % or 100 % — "writing 0 or 100 there would both be a lie". Both tiles get
 * this from the framework rather than from hand-written template logic: `WidgetService.buildUiData`
 * maps a null/undefined field value to the placeholder `'-'` (`widget.service.ts:509-511`), and the
 * `appendPercentage` output control passes that placeholder through untouched instead of appending
 * a percent sign. The quote is therefore rendered by a registered output component and is never
 * turned into a number this component invented.
 *
 * The same null is the reason tile 4's CHART plots `shortfallAmount` rather than the quote: the
 * chart widget folds a null datapoint to 0 (`mvs-widget-chart.component.ts#readAggregate`), which on
 * a percentage axis would draw exactly the forbidden "0 %". `shortfallAmount` is a persisted,
 * never-null column and is the payout-brake indicator (U-03), so it is both safe and the more
 * useful trend. The quote itself is shown in the table beside it, where the placeholder is honest.
 *
 * <h3>Refresh</h3>
 * Two sources, following the sibling panes of this OBC: `MvsObjectService.objectChangeEvent` for
 * `cp.ProvisionAgentTransaction` / `cp.ProvisionAgentReserveCoverage` (a manual booking or transfer
 * moves the balances and the live cover immediately), and `JbSpringBatchIndicatorService.status$`
 * for the running -> not-running transition (the balance and calculation jobs rewrite exactly these
 * figures in the backend and report no object change event). The indicator is activated by the shell
 * `CpDashboardPage` (`jobNamePattern: 'cp%'`); outside that shell `status$` simply never delivers a
 * transition and the pane behaves as before.
 *
 * UI text is German with real umlauts, identifiers and comments are English. Fixed business terms:
 * "Berater" = am.Agent, "Abrechnungskonto" = cp.ProvisionAgent, "Provisionssatz" =
 * cp.ProvisionContractAgent, "Provisionsart" = cp.ProvisionTypeComponent.
 */
@Component({
    selector: 'cp-provision-agent-kpi',
    templateUrl: './cp-provision-agent-kpi.component.html',
    styleUrls: ['./cp-provision-agent-kpi.component.scss'],
    standalone: false
})
export class CpProvisionAgentKpiComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    /** The Abrechnungskonto (`cp.ProvisionAgent`) all five tiles are about. */
    @Input() provisionAgentId: number;

    /**
     * Per chart tile: `true` once its data arrived WITHOUT a single entry, `false` with data,
     * absent while loading. An empty chart widget still draws its axes (0 / 0,5 / 1,0), which reads
     * as „a chart of zeros" rather than „nothing booked yet" — so an empty tile is replaced by an
     * empty-state message. The widget itself stays MOUNTED (only hidden), so an in-place reload
     * after a job reports fresh data through the same `onLoadData` and the chart comes back.
     */
    chartEmpty: { [key in CpKpiChartKey]?: boolean } = {};

    /** Tile 1 — monthly commission totals as a bar chart. */
    provisionMonthChartWidget: WidgetData;

    /** Tile 2 — monthly payout totals as a bar chart. */
    payoutMonthChartWidget: WidgetData;

    /** Tile 3 — the two Rücklagenkonto balances (real + simulated). */
    reserveBalanceWidget: WidgetData;

    /** Tile 4a — shortfall trend per provision run. */
    coverageHistoryChartWidget: WidgetData;

    /** Tile 4b — the four persisted coverage figures per provision run. */
    coverageHistoryTableWidget: WidgetData;

    /** Tile 5 — the live cover of the account, fed by the coverage endpoint. */
    coverageLiveWidget: WidgetData;

    /** True once the coverage endpoint answered with an error — the tile then says so instead of lying. */
    coverageLiveFailed: boolean;

    /** True when no `cp.ProvisionAgentBalance` row exists yet (the balance job has never run). */
    balanceMissing: boolean;

    /**
     * True when the LIVE reserve account is in the minus (`reserveBalance < 0`).
     *
     * A plain sign test on a figure the backend delivered — no business formula is reproduced here.
     * It earns its own statement because a negative Rücklagenkonto is the payout brake (U-03): the
     * caseworker must see it BEFORE starting a payout run, and a minus sign inside a row of four
     * amounts is too easy to read past.
     */
    reserveNegative: boolean;

    /**
     * True when the LIVE cover leaves an uncovered part (`shortfallAmount > 0`), while the reserve
     * account itself is not yet negative. Also a plain comparison against zero.
     */
    shortfallOpen: boolean;

    /** Is a cp job currently running? The figures are then an interim state. */
    jobRunning: boolean;

    /**
     * The mounted history table — only for the in-place reload. `refreshWidget()` reloads WITHOUT
     * re-assigning the binding; a freshly built `WidgetData` would remount the table and throw away
     * the user's sorting and scroll position (same rationale as the sibling coverage pane).
     */
    @ViewChild('coverageHistoryTableRef') coverageHistoryTableComponent: MvsWidgetComponent;

    protected objectChangeSubscription: Subscription;
    protected statusSubscription: Subscription;
    protected coverageSubscription: Subscription;
    protected balanceSubscription: Subscription;
    protected jobWasRunning: boolean;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected coverageService: CpProvisionAgentCoverageService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /**
     * A manual booking, a transfer or a freshly written coverage row changes what these tiles show.
     */
    protected subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentTransaction'
                || change.objectType === 'cp.ProvisionAgentReserveCoverage') {
                this.refreshComponent();
            }
        });
    }

    /** Subscription to the job indicator; reload on the running -> not-running transition. */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshComponent();
            }
        });
    }

    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.clearWidgets();
            this.initialized = true;
            return;
        }

        this.busy = true;

        this.buildTransactionChartWidgets();
        this.buildCoverageHistoryWidgets();
        this.loadReserveBalance();
        this.loadLiveCoverage();

        this.initialized = true;
        this.busy = false;
    }

    /** A chart tile delivered its data — remember whether it was empty (see {@link chartEmpty}). */
    handleChartLoaded(key: CpKpiChartKey, dtoList: DtoList): void {
        this.chartEmpty = {...this.chartEmpty, [key]: !dtoList?.entries?.length};
    }

    protected clearWidgets(): void {
        this.chartEmpty = {};
        this.provisionMonthChartWidget = null;
        this.payoutMonthChartWidget = null;
        this.reserveBalanceWidget = null;
        this.coverageHistoryChartWidget = null;
        this.coverageHistoryTableWidget = null;
        this.coverageLiveWidget = null;
        this.coverageLiveFailed = false;
        this.balanceMissing = false;
        this.reserveNegative = false;
        this.shortfallOpen = false;
    }

    // ================================================================================= //
    // Tiles 1 + 2 — monthly aggregation over cp.ProvisionAgentTransaction
    // ================================================================================= //

    /**
     * Builds the two monthly charts. Each gets its OWN query instance: a shared literal would be
     * mutated by two widgets.
     */
    protected buildTransactionChartWidgets(): void {

        this.chartEmpty = {...this.chartEmpty, provisions: undefined, payouts: undefined};

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Provisionen pro Monat" of one Abrechnungskonto — QL groupBy on
         *         cp.ProvisionAgentTransaction, sum(amount) per month of the valuta date, ACTIVE
         *         bookings only, so the tile shows what was really booked rather than a preview.
         */
        this.provisionMonthChartWidget = WidgetFactory.createWidgetListQl(
            'cp.agent.kpi.provisions.month.chart',
            'Provisionen pro Monat',
            'chart',
            this.buildMonthlyTransactionQuery('cp.agent.kpi.provisions.month', false),
            WidgetDataParam.create('chartType', 'bar'),
            WidgetDataParam.create('chartField1st', 'e.transactionMonth'),
            WidgetDataParam.create('chartFieldAggregate', 'e.transactionAmount'),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('chartStacked', false),
            WidgetDataParam.create('size', 'M')
        );

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Auszahlungen pro Monat" — the same monthly aggregation narrowed to
         *         event = PAYOUT, so the caseworker sees what actually left the Abrechnungskonto
         *         next to what was earned on it.
         */
        this.payoutMonthChartWidget = WidgetFactory.createWidgetListQl(
            'cp.agent.kpi.payouts.month.chart',
            'Auszahlungen pro Monat',
            'chart',
            this.buildMonthlyTransactionQuery('cp.agent.kpi.payouts.month', true),
            WidgetDataParam.create('chartType', 'bar'),
            WidgetDataParam.create('chartField1st', 'e.transactionMonth'),
            WidgetDataParam.create('chartFieldAggregate', 'e.transactionAmount'),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('chartStacked', false),
            WidgetDataParam.create('size', 'M')
        );
    }

    /**
     * The monthly QL aggregation over `cp.ProvisionAgentTransaction`.
     *
     * `status = ACTIVE` is a business necessity, not a tidy-up: a CALCULATED booking is a preview
     * and has not moved any money, so it must not appear in a monthly total.
     *
     * `dateGroup` resolves only over a real date COLUMN. `valutaDate` is a `LocalDate` column and is
     * therefore suitable, unlike audit/instant fields.
     *
     * @param queryName  QL query name, unique per widget
     * @param payoutOnly true narrows the aggregation to `event = PAYOUT` (tile 2)
     */
    protected buildMonthlyTransactionQuery(queryName: string, payoutOnly: boolean): QlQueryDto {

        const transactionMonthField: CpKpiQlDateGroupField = {
            name: 'valutaDate',
            as: 'transactionMonth',
            label: 'Monat',
            dataType: 'LocalDate',
            fieldType: 'dateGroup',
            dateGroupPrecision: 'month'
        };

        const fields: QlQueryContentFieldDto[] = [
            // Double cast: the documented workaround of CpKpiQlDateGroupField. `fieldType` is
            // narrowed to 'regular' | 'function' in the core DTO, so the direct cast is
            // structurally invalid and the detour via `unknown` is necessary. The payload itself is
            // correct — the backend reads exactly these two attributes.
            transactionMonthField as unknown as QlQueryContentFieldDto,
            {
                name: 'amount',
                as: 'transactionAmount',
                function: 'sum',
                label: 'Summe',
                dataType: 'BigDecimal',
                fieldType: 'function'
            },
            {
                name: '*',
                as: 'transactionCount',
                function: 'count',
                label: 'Anzahl',
                dataType: 'Long',
                fieldType: 'function'
            }
        ];

        const fixedFilters: FilterCriteria[] = [
            FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId),
            FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, ProvisionTransactionStatus.ACTIVE)
        ];

        if (payoutOnly) {
            fixedFilters.push(
                FilterCriteria.create('e.event', FilterCriteria.cOperatorEqual, ProvisionTransactionEvent.PAYOUT));
        }

        return {
            name: queryName,
            type: 'view',
            start: {
                type: 'groupBy',
                name: 'cp.ProvisionAgentTransaction',
                as: 'e',
                fields: fields
            },
            fixedFilters: fixedFilters,
            sortings: [new Sorting('e.transactionMonth', true)]
        };
    }

    // ================================================================================= //
    // Tile 3 — Rücklagenkonto balance
    // ================================================================================= //

    /**
     * Loads the `cp.ProvisionAgentBalance` row of the account and builds the read-only data widget
     * on it.
     *
     * The balance row is 1:1 with the account but carries its OWN id, so it has to be looked up by
     * a filter on `provisionAgent` before a data widget can address it — the same data path the
     * "Konten/Salden" pane already uses.
     */
    protected loadReserveBalance(): void {

        const objectRequestList = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('provisionAgent', FilterCriteria.cOperatorEqual, this.provisionAgentId)],
            null);

        this.balanceSubscription?.unsubscribe();

        this.balanceSubscription = this.coreService.getCrudService('cp.ProvisionAgentBalance')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    const entries = dtoList?.entries as ProvisionAgentBalanceDto[];
                    const balanceDto = entries && entries.length ? entries[0] : null;
                    this.buildReserveBalanceWidget(balanceDto);
                },
                error: () => {
                    this.buildReserveBalanceWidget(null);
                }
            });
    }

    /**
     * Builds tile 3.
     *
     * The two reserve balances are shown as TWO FIELDS, not as one derived number: the real balance
     * (ACTIVE bookings) and the simulated one (ACTIVE + CALCULATED) answer different questions and
     * combining them would invent a figure the module does not have.
     */
    protected buildReserveBalanceWidget(balanceDto: ProvisionAgentBalanceDto): void {

        this.balanceMissing = !balanceDto || !balanceDto.id;

        if (this.balanceMissing) {
            this.reserveBalanceWidget = null;
            return;
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Rücklagenkonto" — the real and the simulated reserve balance of the
         *         Abrechnungskonto from cp.ProvisionAgentBalance, read-only and layout-pinned to
         *         those two columns so the tile stays a tile instead of the full balance row.
         */
        this.reserveBalanceWidget = WidgetFactory.createWidgetEntityData(
            'cp.agent.kpi.reserve.balance.data',
            'Rücklagenkonto',
            'cp.ProvisionAgentBalance',
            balanceDto.id,
            WidgetDataParam.create('widgetForm', CpProvisionAgentKpiComponent.buildKpiWidgetForm([
                {field: 'reserveBalance', label: 'Rücklage', outputField: 'currencyEuro'},
                {field: 'reserveSimulatedBalance', label: 'Rücklage (simuliert)', outputField: 'currencyEuro'}
            ])),
            WidgetDataParam.create('columns', 2),
            // Derived balances must not be editable from a KPI tile.
            WidgetDataParam.create('formEdit', false),
            WidgetDataParam.create('allowDoubleClick', false),
            WidgetDataParam.create('size', 'S')
        );
    }

    // ================================================================================= //
    // Tile 4 — reserve versus liability per provision run
    // ================================================================================= //

    /**
     * Builds the two history widgets over `cp.ProvisionAgentReserveCoverage`, sorted by run.
     *
     * All four figures are PERSISTED columns written by the run's post-processing — nothing is
     * computed here. When the table is already mounted it is reloaded in place instead of rebuilt,
     * so the user keeps sorting and scroll position.
     */
    protected buildCoverageHistoryWidgets(): void {

        this.chartEmpty = {...this.chartEmpty, coverage: undefined};

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Unterdeckung je Provisionslauf" — the persisted shortfallAmount of
         *         cp.ProvisionAgentReserveCoverage as a trend; it is the payout-brake indicator and,
         *         unlike the coverage quote, never null, so the chart cannot draw a misleading zero.
         */
        this.coverageHistoryChartWidget = WidgetFactory.createWidgetListQl(
            'cp.agent.kpi.coverage.history.chart',
            'Unterdeckung je Provisionslauf',
            'chart',
            this.buildCoverageHistoryQuery('cp.agent.kpi.coverage.history.chart'),
            WidgetDataParam.create('chartType', 'bar'),
            WidgetDataParam.create('chartField1st', 'pr.name'),
            WidgetDataParam.create('chartFieldAggregate', 'e.shortfallAmount'),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('chartStacked', false),
            WidgetDataParam.create('size', 'M')
        );

        if (this.coverageHistoryTableComponent && this.coverageHistoryTableWidget) {
            this.coverageHistoryTableComponent.refreshWidget();
            return;
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Rücklage vs. Haftung je Lauf" — the four persisted coverage columns of
         *         cp.ProvisionAgentReserveCoverage per provision run, including the nullable
         *         coverage quote, which the framework renders as a dash instead of a made-up percent.
         */
        this.coverageHistoryTableWidget = WidgetFactory.createWidgetTableQl(
            'cp.agent.kpi.coverage.history.table',
            'Rücklage vs. Haftung je Lauf',
            this.buildCoverageHistoryQuery('cp.agent.kpi.coverage.history.table'),
            WidgetDataParam.create('widgetForm', CpProvisionAgentKpiComponent.buildKpiWidgetForm([
                {field: 'pr.name', label: 'Provisionslauf'},
                {field: 'e.openLiabilityAmount', label: 'Offene Haftung', outputField: 'currencyEuro'},
                {field: 'e.reserveBalance', label: 'Rücklage', outputField: 'currencyEuro'},
                {field: 'e.coverageQuote', label: 'Deckungsquote', outputField: 'appendPercentage'},
                {field: 'e.shortfallAmount', label: 'Unterdeckung', outputField: 'currencyEuro'}
            ])),
            WidgetDataParam.create('paging', 12),
            WidgetDataParam.create('size', 'S')
        );
    }

    /**
     * The coverage history query. Its own instance per widget — chart and table must not share one
     * query object.
     *
     * @param queryName QL query name, unique per widget
     */
    protected buildCoverageHistoryQuery(queryName: string): QlQueryDto {
        return {
            name: queryName,
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentReserveCoverage',
                as: 'e',
                joins: [
                    {type: 'entity', name: 'provisionRun', as: 'pr', joinType: 'left'}
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId)
            ],
            sortings: [new Sorting('e.provisionRunId', true)]
        };
    }

    // ================================================================================= //
    // Tile 5 — the LIVE cover of the account
    // ================================================================================= //

    /**
     * Reads `GET /mvsa/cp/provisionAgents/{id}/coverage` and feeds the answer into a TRANSIENT data
     * widget.
     *
     * Transient rather than entity/QL because the payload is not a row of any entity: the quote is a
     * quotient of two aggregates over two entities, which QL cannot express, and which must not be
     * divided in the browser. The widget therefore receives the endpoint DTO as its data and an
     * `MvsFormDto` built here as its field metadata — the documented shape for a widget without an
     * entity behind it (`createWidgetTransient`, `widget.factory.ts:807`).
     */
    protected loadLiveCoverage(): void {

        this.coverageSubscription?.unsubscribe();

        this.coverageSubscription = this.coverageService.getCoverage(this.provisionAgentId)
            .subscribe({
                next: (coverage) => {
                    this.coverageLiveFailed = false;
                    this.evaluateCoverageState(coverage);
                    this.buildLiveCoverageWidget(coverage);
                },
                error: () => {
                    this.coverageLiveFailed = true;
                    this.coverageLiveWidget = null;
                    this.reserveNegative = false;
                    this.shortfallOpen = false;
                }
            });
    }

    /**
     * Derives the two emphasis flags of tile 5 from the endpoint answer.
     *
     * Both are SIGN TESTS on figures the backend already computed — no quote, no difference and no
     * threshold is worked out here. `reserveBalance` may legitimately be negative (the account owes
     * money on the Rücklagenkonto) and `shortfallAmount` is never negative, so the two cases are
     * mutually exclusive by construction and are ranked: the negative account is the more severe
     * statement and wins.
     *
     * A missing figure is NOT treated as a problem — as long as nothing has been computed, no alarm
     * may be raised.
     */
    protected evaluateCoverageState(coverage: ProvisionAgentCoverageDto): void {

        const reserve = coverage?.reserveBalance;
        const shortfall = coverage?.shortfallAmount;

        this.reserveNegative = reserve != null && reserve < 0;
        this.shortfallOpen = !this.reserveNegative && shortfall != null && shortfall > 0;
    }

    /**
     * Builds tile 5 from the endpoint answer.
     *
     * The four fields are passed through UNCHANGED. `coverageQuote` keeps its null: the widget
     * pipeline renders a null field value as the placeholder `'-'`, and the `appendPercentage`
     * output control leaves that placeholder alone rather than appending a percent sign. Neither
     * 0 % nor 100 % can appear.
     */
    protected buildLiveCoverageWidget(coverage: ProvisionAgentCoverageDto): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        const openLiabilityField = MvsFormFieldDto.createBasic(
            'openLiabilityAmount', 'amount', 'Offene Haftung', 'read');
        openLiabilityField.dataType = 'BigDecimal';
        openLiabilityField.uiOutputControl = 'currencyEuro';
        openLiabilityField.uiDescription = 'Summe der Haftungsbeträge in den Status ACTIVE und SUSPENDED.';
        openLiabilityField.order = 10;
        formFields['openLiabilityAmount'] = openLiabilityField;

        const reserveBalanceField = MvsFormFieldDto.createBasic(
            'reserveBalance', 'amount', 'Rücklage', 'read');
        reserveBalanceField.dataType = 'BigDecimal';
        reserveBalanceField.uiOutputControl = 'currencyEuro';
        reserveBalanceField.uiDescription = 'Aktiver Saldo des Rücklagenkontos; kann negativ sein.';
        reserveBalanceField.order = 20;
        formFields['reserveBalance'] = reserveBalanceField;

        const coverageQuoteField = MvsFormFieldDto.createBasic(
            'coverageQuote', 'number', 'Deckungsquote', 'read');
        coverageQuoteField.dataType = 'BigDecimal';
        coverageQuoteField.uiOutputControl = 'appendPercentage';
        coverageQuoteField.uiDescription = 'Ohne offene Haftung gibt es keine Quote — dann steht hier ein Strich.';
        coverageQuoteField.order = 30;
        formFields['coverageQuote'] = coverageQuoteField;

        const shortfallField = MvsFormFieldDto.createBasic(
            'shortfallAmount', 'amount', 'Unterdeckung', 'read');
        shortfallField.dataType = 'BigDecimal';
        shortfallField.uiOutputControl = 'currencyEuro';
        shortfallField.uiDescription = 'Nicht durch die Rücklage gedeckter Anteil der offenen Haftung.';
        shortfallField.order = 40;
        formFields['shortfallAmount'] = shortfallField;

        form.formFields = formFields;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: KPI tile "Deckungsquote (aktuell)" — the live reserve-versus-liability cover of the
         *         Abrechnungskonto from GET /cp/provisionAgents/{id}/coverage, rendered as a transient
         *         data widget because the quote is a cross-entity quotient QL cannot project and the
         *         frontend must not compute.
         */
        this.coverageLiveWidget = WidgetFactory.createWidgetTransient(
            'cp.agent.kpi.coverage.live.data',
            'Deckungsquote (aktuell)',
            'data',
            'transient',
            'transient',
            'cp.ProvisionAgent',
            coverage,
            WidgetDataParam.create('form', form),
            WidgetDataParam.create('columns', 4),
            // A transient payload carries no entity id, so the generic edit path has nothing to open.
            WidgetDataParam.create('formEdit', false),
            WidgetDataParam.create('allowDoubleClick', false),
            WidgetDataParam.create('size', 'S')
        );
    }

    // ================================================================================= //
    // Layout helper
    // ================================================================================= //

    /**
     * Builds the curated layout of a KPI widget — the field set, its order, the German labels and
     * the output control per field.
     *
     * WHY EVERY KPI WIDGET NEEDS ONE: without a layout the generic widget renders EVERY field the
     * form carries, which for `cp.ProvisionAgentBalance` or a QL projection means a wall of columns
     * instead of a tile. `hideFields` + `fieldsConfigured` are the documented "only the configured
     * fields are shown" switch pair.
     *
     * It is a local sibling of `buildCpWidgetForm` (cp/service/domain/cp-widget-column.ts) rather
     * than a call into it, because the KPI tiles need the OUTPUT CONTROL per field — euro for the
     * amounts, percent for the quote — which that helper does not carry, and widening a shared cp
     * file is outside this work package.
     *
     * Every field id must exist in the widget's form: the plain attribute name on an entity widget,
     * `e.<attr>` / `<joinAs>.<attr>` on a QL one. An id that matches nothing is silently dropped
     * with no compile error.
     *
     * @param fields the visible fields, in the order they should appear
     */
    protected static buildKpiWidgetForm(fields: CpKpiWidgetField[]): WidgetForm {

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        fields.forEach((entry, index) => {
            const layoutField = WidgetConfigFormField.create(entry.field, '', '');
            layoutField.uiLabel = entry.label;
            layoutField.uiPriority = index;

            if (entry.outputField) {
                layoutField.uiOutputField = entry.outputField;
            }

            layout.addField(layoutField);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);

        return widgetForm;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            // A different account invalidates every mounted widget, so nothing is reloaded in place.
            this.clearWidgets();
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
        this.coverageSubscription?.unsubscribe();
        this.balanceSubscription?.unsubscribe();
    }

}
